import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

const shellStyles = css`
  :host {
    display: block;
    width: 100%;
    margin-top: 10px;
    font-family: var(--cg-font);
    --sl-font-sans: var(--cg-font);
  }

  *,
  *::before,
  *::after {
    font-family: var(--cg-font);
  }

  button,
  input,
  textarea,
  select {
    font: inherit;
  }

  .widget {
    overflow: hidden;
    border: 1px solid var(--cg-border);
    border-radius: 12px;
    background: var(--cg-surface);
    color: var(--cg-text);
  }

  .header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    border-bottom: 1px solid var(--cg-border);
  }

  .icon {
    width: 40px;
    height: 40px;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border-radius: 10px;
  }

  .title {
    margin: 0;
    color: var(--cg-text);
    font-size: 14px;
    line-height: 1.3;
    font-weight: 600;
  }

  .subtitle {
    margin: 4px 0 0;
    color: var(--cg-muted);
    font-size: 12px;
    line-height: 1.45;
  }

  .body {
    display: grid;
    gap: 10px;
    padding: 14px;
  }

  .field {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid var(--cg-border);
    border-radius: 10px;
    background: var(--cg-bg);
    padding: 10px;
  }

  .field-icon {
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    flex: 0 0 auto;
  }

  .label {
    color: var(--cg-muted);
    font-size: 11px;
    font-weight: 500;
  }

  .value {
    margin-top: 2px;
    color: var(--cg-text);
    font-size: 13px;
    font-weight: 500;
  }
`;

const userIcon = html`<svg
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
  <circle cx="12" cy="7" r="4" />
</svg>`;

const mailIcon = html`<svg
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <rect width="20" height="16" x="2" y="4" rx="2" />
  <path d="m22 7-10 6L2 7" />
</svg>`;

const phoneIcon = html`<svg
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.63 2.61a2 2 0 0 1-.45 2.11L8 9.69a16 16 0 0 0 6.31 6.31l1.25-1.25a2 2 0 0 1 2.11-.45c.84.3 1.71.51 2.61.63A2 2 0 0 1 22 16.92z" />
</svg>`;

const messageIcon = html`<svg
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
</svg>`;

@customElement("cg-lead-widget")
export class CGLeadWidget extends LitElement {
  static override styles = [
    shellStyles,
    css`
      .icon {
        border: 1px solid #bbf7d0;
        background: #ecfdf5;
        color: #059669;
      }
      .field-icon {
        color: #059669;
      }
    `,
  ];

  @property({ type: String }) override title = "Share your contact details";

  override render() {
    return html`
      <div class="widget">
        <div class="header">
          <div class="icon">${userIcon}</div>
          <div>
            <p class="title">${this.title}</p>
            <p class="subtitle">The team can follow up with the right next step.</p>
          </div>
        </div>
        <div class="body">
          ${this.renderField("Name", "Jane Doe", userIcon)}
          ${this.renderField("Email", "jane@example.com", mailIcon)}
          ${this.renderField("Phone", "+1 555 0100", phoneIcon)}
        </div>
      </div>
    `;
  }

  private renderField(label: string, value: string, icon: unknown) {
    return html`
      <div class="field">
        <div class="field-icon">${icon}</div>
        <div>
          <div class="label">${label}</div>
          <div class="value">${value}</div>
        </div>
      </div>
    `;
  }
}

@customElement("cg-support-ticket-widget")
export class CGSupportTicketWidget extends LitElement {
  static override styles = [
    shellStyles,
    css`
      .icon {
        border: 1px solid #fde68a;
        background: #fffbeb;
        color: #d97706;
      }
      .field-icon {
        color: #d97706;
      }
    `,
  ];

  @property({ type: String }) override title = "Create a support ticket";

  override render() {
    return html`
      <div class="widget">
        <div class="header">
          <div class="icon">${messageIcon}</div>
          <div>
            <p class="title">${this.title}</p>
            <p class="subtitle">Capture the issue clearly so support can respond faster.</p>
          </div>
        </div>
        <div class="body">
          ${this.renderField("Name", "Customer name", userIcon)}
          ${this.renderField("Email", "customer@example.com", mailIcon)}
          ${this.renderField("Issue", "Describe what happened", messageIcon)}
        </div>
      </div>
    `;
  }

  private renderField(label: string, value: string, icon: unknown) {
    return html`
      <div class="field">
        <div class="field-icon">${icon}</div>
        <div>
          <div class="label">${label}</div>
          <div class="value">${value}</div>
        </div>
      </div>
    `;
  }
}
